/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import isnan from '@stdlib/math/base/assert/is-nan';
import ln from '@stdlib/math/base/special/ln';
import GAMMA from '@stdlib/constants/float64/eulergamma';
import PI from '@stdlib/constants/float64/pi';


// VARIABLES //

var ONE_PLUS_THREE_GAMMA = 1.0 + ( 3.0 * GAMMA );
var PI_TIMES_SIXTEEN = 16.0 * PI;


// MAIN //

/**
* Returns the differential entropy for a Lévy distribution with location `mu` and scale `c`.
*
* @param {number} mu - location parameter
* @param {PositiveNumber} c - scale parameter
* @returns {number} entropy
*
* @example
* var y = entropy( 0.0, 1.0 );
* // returns ~3.324
*
* @example
* var y = entropy( 5.0, 2.0 );
* // returns ~4.018
*
* @example
* var y = entropy( NaN, 1.0 );
* // returns NaN
*
* @example
* var y = entropy( 0.0, NaN );
* // returns NaN
*
* @example
* var y = entropy( 0.0, 0.0 );
* // returns NaN
*/
function entropy( mu, c ) {
	if (
		isnan( mu ) ||
		isnan( c ) ||
		c <= 0.0
	) {
		return NaN;
	}
	return ( ONE_PLUS_THREE_GAMMA + ln( c*c*PI_TIMES_SIXTEEN ) ) / 2.0;
}


// EXPORTS //

export default entropy;
